import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useToast } from '../../hooks/use-toast';
import { useTheme } from '../../components/ui/theme-provider';
import { cn } from '../../lib/utils';
import { Download, MapPin, Clock, AlertCircle, CheckCircle, XCircle, RefreshCw, Edit, Eye, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit, doc, updateDoc, writeBatch, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { geocodePostcode, geocodeLocation } from '../../utils/geolocationService';
import JobEditModal from '../../components/admin/JobEditModal';
import { extractEmploymentType } from '../../utils/jobClassification';

const RSS_SOURCES = {
  BDJ_HYGIENIST_THERAPIST: {
    name: 'BDJ Jobs - Hygienist & Therapist',
    url: 'https://www.bdjjobs.com/jobsrss/?JobType=16&countrycode=GB',
    type: 'hygienist_therapist',
    role: 'Hygienist/Therapist'
  },
  BDJ_LOCUM_DENTIST: {
    name: 'BDJ Jobs - Locum Dentist',
    url: 'https://www.bdjjobs.com/jobsrss/?JobType=12&countrycode=GB',
    type: 'locum_dentist',
    role: 'Locum Dentist'
  },
  BDJ_DENTAL_NURSE: {
    name: 'BDJ Jobs - Dental Nurse',
    url: 'https://www.bdjjobs.com/jobsrss/?JobType=17&countrycode=GB',
    type: 'dental_nurse',
    role: 'Dental Nurse'
  }
};

const RSSJobManagerPage = () => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [results, setResults] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [editingJob, setEditingJob] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const { toast } = useToast();
  const { theme } = useTheme();

  // Load recent RSS jobs on component mount
  React.useEffect(() => {
    loadRecentRSSJobs();
  }, []);

  // Enhanced recent jobs query to show ALL RSS jobs (not just last 20)
  const loadRecentRSSJobs = async () => {
    try {
      const rssJobsRef = collection(db, 'rss_jobs');
      const q = query(
        rssJobsRef,
        orderBy('scrapedAt', 'desc')
        // REMOVED: limit(20) - now shows ALL RSS jobs
      );
      const snapshot = await getDocs(q);
      const jobs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Flag jobs that need attention
        needsLocationReview: !doc.data().latitude || !doc.data().longitude || !doc.data().location?.trim(),
        needsManualReview: doc.data().needsManualReview || false,
        // NEW: Flag jobs as non-UK if location or postcode is outside UK
        isNonUK: (() => {
          // Check for Channel Islands, Ireland, or obvious non-UK
          const loc = (doc.data().location || '').toLowerCase();
          const pc = (doc.data().postcode || '').toUpperCase();
          // Channel Islands postcodes: JE (Jersey), GY (Guernsey), IM (Isle of Man)
          if (/jersey|guernsey|isle of man|dublin|ireland|republic of ireland/.test(loc)) return true;
          if (/^(JE|GY|IM)\d/.test(pc)) return true;
          // Irish Eircodes: start with a letter, then digit, then 4 chars (e.g. D02 X285)
          if (/^[A-Z]\d{1,2}\s?[A-Z0-9]{4}$/.test(pc) && !/^([A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2})$/.test(pc)) return true;
          // If country field exists and is not UK
          if (doc.data().country && !/uk|united kingdom|gb|great britain/i.test(doc.data().country)) return true;
          return false;
        })()
      }));
      setRecentJobs(jobs);
      console.log(`Loaded ${jobs.length} RSS jobs from database`);
    } catch (error) {
      console.error('Error loading recent RSS jobs:', error);
    }
  };

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const extractPostcode = (locationText) => {
    if (!locationText) return null;
    
    // More strict UK postcode regex - must be valid format
    const postcodePatterns = [
      // Full postcode format with proper validation
      /\b([A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2})\b/gi,
    ];
    
    console.log('Extracting postcode from:', locationText);
    
    for (const pattern of postcodePatterns) {
      const matches = locationText.match(pattern);
      if (matches && matches.length > 0) {
        for (const match of matches) {
          const postcode = match.trim().toUpperCase();
          
          // Additional validation - check if it looks like a real postcode
          // Valid UK postcodes: SW1A 1AA, M1 1AA, B33 8TH, etc.
          // Invalid: FOR, AND, THE, etc.
          const isValidPostcodeFormat = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/.test(postcode);
          const isNotCommonWord = !['FOR', 'AND', 'THE', 'YOU', 'CAN', 'ARE', 'WAS', 'NEW', 'OLD'].includes(postcode.replace(/\s/g, ''));
          
          if (isValidPostcodeFormat && isNotCommonWord) {
            console.log('Found valid postcode:', postcode);
            return postcode;
          }
        }
      }
    }
    
    console.log('No valid postcode found in:', locationText);
    return null;
  };

  // New function specifically for geocoding city/location names
  const geocodeLocation = async (locationName) => {
    try {
      // Use a different geocoding service for general locations
      // We can use the same Postcodes.io service but with a different endpoint
      const cleanLocation = locationName.trim();
      
      // Try multiple approaches for location geocoding
      const geocodingAttempts = [
        // Try with Nominatim (OpenStreetMap) - free and good for UK locations
        async () => {
          const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=gb&q=${encodeURIComponent(cleanLocation)}`;
          const response = await fetch(nominatimUrl, {
            headers: {
              'User-Agent': 'HygienistInvoiceApp/1.0' // Required by Nominatim
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data && data.length > 0) {
              return {
                latitude: parseFloat(data[0].lat),
                longitude: parseFloat(data[0].lon)
              };
            }
          }
          return null;
        },
        
        // Fallback: Try with postcodes.io places endpoint if available
        async () => {
          try {
            const response = await fetch(`https://api.postcodes.io/places?q=${encodeURIComponent(cleanLocation)}`);
            if (response.ok) {
              const data = await response.json();
              if (data.status === 200 && data.result && data.result.length > 0) {
                const place = data.result[0];
                return {
                  latitude: place.latitude,
                  longitude: place.longitude
                };
              }
            }
          } catch (error) {
            console.warn('Postcodes.io places endpoint failed:', error);
          }
          return null;
        }
      ];
      
      // Try each geocoding method
      for (const attempt of geocodingAttempts) {
        try {
          const result = await attempt();
          if (result && result.latitude && result.longitude) {
            console.log(`Successfully geocoded location "${cleanLocation}":`, result);
            return result;
          }
        } catch (error) {
          console.warn(`Geocoding attempt failed for "${cleanLocation}":`, error);
          continue;
        }
      }

      console.warn(`No geocoding results for location: ${cleanLocation}`);
      return null;
    } catch (error) {
      console.error(`Error geocoding location "${locationName}":`, error);
      return null;
    }
  };

  const fetchRSSFeed = async (source) => {
    setCurrentStep('Fetching RSS feed...');
    setProgress(10);

    try {
      const fetchRssFeed = httpsCallable(functions, 'fetchRssFeed');
      const response = await fetchRssFeed({ sourceUrl: source.url });
      const xmlText = response?.data?.xmlText;

      if (!xmlText || xmlText.length < 100) {
        throw new Error('Invalid or empty RSS content received');
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('Invalid XML format received');
      }

      const items = Array.from(xmlDoc.querySelectorAll('item'));
      console.log(`Successfully fetched ${items.length} RSS items via backend fetchRssFeed`);

      return items.map(item => ({
        title: item.querySelector('title')?.textContent || '',
        link: item.querySelector('link')?.textContent || '',
        description: item.querySelector('description')?.textContent || '',
        pubDate: item.querySelector('pubDate')?.textContent || '',
      }));
    } catch (error) {
      const backendMessage = error?.message || error?.details || 'Unknown error';
      throw new Error(`RSS fetch failed via backend endpoint: ${backendMessage}`);
    }
  };

  const extractJobDetailsFromRSS = (rssItem, sourceRole) => {
    // Enhanced extraction from RSS description with role-specific processing
    const description = rssItem.description || '';
    const title = rssItem.title || 'Job Position';
    
    console.log('Extracting from RSS:', { title, description, sourceRole });
    
    // Role-specific title cleaning and role detection
    let detectedRole = sourceRole; // Default to source role
    let cleanTitle = title;
    
    // Enhanced role detection from title content
    const rolePatterns = {
      'Hygienist/Therapist': [
        /hygienist/i, /therapist/i, /oral health/i, /dental hygiene/i
      ],
      'Locum Dentist': [
        /locum/i, /dentist/i, /dental surgeon/i, /gdp/i, /general dental/i
      ],
      'Dental Nurse': [
        /dental nurse/i, /qualified dental nurse/i, /trainee dental nurse/i, /dental nursing/i
      ],
      'Practice Manager': [
        /practice manager/i, /dental practice manager/i, /management/i
      ],
      'Receptionist': [
        /receptionist/i, /dental receptionist/i, /front desk/i
      ]
    };
    
    // Try to detect more specific role from content
    for (const [role, patterns] of Object.entries(rolePatterns)) {
      if (patterns.some(pattern => pattern.test(title + ' ' + description))) {
        detectedRole = role;
        break;
      }
    }
    
    // Enhanced location extraction - location is typically at the end of description
    let location = '';
    
    // FIXED: Add missing knownValidLocations array
    const knownValidLocations = [
      'Watford', 'Weymouth', 'Belfast', 'Cork', 'Derry', 'Hull', 'Bath', 'York',
      'Chester', 'Derby', 'Exeter', 'Ipswich', 'Lincoln', 'Norwich', 'Oxford',
      'Preston', 'Salford', 'Stoke', 'Sunderland', 'Wigan', 'Antrim', 'Fife',
      'Glenrothes', 'Wirksworth', 'Clevedon', 'Barnstaple', 'Chelsea', 'Omagh',
      'Bridport', 'Dorset', 'Devon', 'Suffolk', 'Essex', 'Kent', 'Surrey',
      'London', 'Birmingham', 'Manchester', 'Liverpool', 'Newcastle', 'Glasgow',
      'Edinburgh', 'Cardiff', 'Bristol', 'Leeds', 'Sheffield', 'Nottingham'
    ];
    
    // IMPROVED: Better location extraction patterns with specific fixes for common issues
    const locationPatterns = [
      // Pattern 1: City, County at end (e.g. "Ipswich, Suffolk")
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)\s*$/,
      // Pattern 2: Just city name at end (minimum 3 chars, but allow some specific short names)
      /\s([A-Z][a-z]{2,}(?:\s+[A-Z][a-z]+)*)\s*$/,
      // Pattern 3: Location in parentheses (e.g. "London (Central)")
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*\s*\([^)]+\))\s*$/,
      // Pattern 4: Multiple cities mentioned (take the last valid one)
      /([A-Z][a-z]{2,}(?:\s+[A-Z][a-z]+)*)\s$/
    ];
    
    // Try each pattern and validate the result
    for (const pattern of locationPatterns) {
      const match = description.match(pattern);
      if (match && match[1]) {
        const potentialLocation = match[1].trim();
        
        // IMPROVED: More specific filtering with enhanced allowlist for common UK locations
        const excludeWords = [
          'Care', 'Team', 'Jobs', 'Recruitment', 'Practice', 'Dental', 'Clinic', 
          'Here', 'Services', 'Apply', 'Contact', 'Great', 'Excellent', 'Fantastic',
          'New', 'Old', 'High', 'Low', 'Good', 'Best', 'Top', 'Main', 'Full',
          'Part', 'Time', 'Week', 'Day', 'Hour', 'Rate', 'Pay', 'Salary',
          'Specialist', 'Dentistry', 'Established', 'Brand', 'Everything',
          'Heart', 'Environ', 'Created', 'Beautiful', 'Beautifu', 'Fixed',
          'Term', 'Employed', 'Therapist', 'Hygienist', 'Month', 'Contract',
          'Nurse', 'Nursing', 'Dentist', 'Locum'
        ];
        
        // Check if this is a known valid location (case-insensitive)
        const isKnownValid = knownValidLocations.some(validLoc => 
          potentialLocation.toLowerCase().includes(validLoc.toLowerCase())
        );
        
        // Check if the location contains any exclude words (but not if it's known valid)
        const hasExcludeWords = !isKnownValid && excludeWords.some(word => 
          potentialLocation.toLowerCase().includes(word.toLowerCase())
        );
        
        // FIXED: Add missing validation variables
        const isValidLength = potentialLocation.length >= 3 && potentialLocation.length <= 50;
        const hasValidFormat = /^[A-Za-z\s(),&-]+$/.test(potentialLocation);
        
        // IMPROVED: Additional checks for common false positives
        const isNotGenericWord = !['Fixed', 'Term', 'Month', 'Week', 'Hour', 'Den', 'Nor', 'Ortho', 'Nurse', 'Dentist'].includes(potentialLocation);
        
        // Accept if it's known valid OR passes all other checks
        if ((isKnownValid && isValidLength && hasValidFormat) || 
            (!hasExcludeWords && isValidLength && hasValidFormat && isNotGenericWord)) {
          location = potentialLocation;
          console.log('Found valid location:', location);
          break;
        } else {
          console.log('Rejected potential location:', potentialLocation, {
            isKnownValid,
            hasExcludeWords,
            isValidLength,
            hasValidFormat,
            isNotGenericWord
          });
        }
      }
    }
    
    // If location not found, try to extract from text content
    if (!location) {
      // FIXED: Use description instead of undefined textContent
      const textContent = description;
      const locationPatterns = [
        /Location:\s*([^,\n]+)/i,
        /Based in:\s*([^,\n]+)/i,
        /Area:\s*([^,\n]+)/i,
        /\b([A-Z][a-z]+) ([A-Z][a-z]+(?:, [A-Z][a-z]+)*)\b/g // City, County pattern
      ];
      
      for (const pattern of locationPatterns) {
        const match = textContent.match(pattern);
        if (match) {
          location = match[1] || match[0];
          break;
        }
      }
    }
    
    // Extract salary
    let salary = '';
    const salaryPatterns = [
      // £45 per hour
      /(£\d+(?:\.\d{2})?\s+per\s+hour)/i,
      // £X or Y% of treatments
      /(£\d+(?:\.\d{2})?\s+per\s+hour\s+or\s+\d+%\s+of\s+treatments?)/i,
      // General £X format
      /(£[\d,]+(?:\.\d{2})?(?:\s*-\s*£[\d,]+(?:\.\d{2})?)?)/i,
      // Percentage only
      /(\d+%\s+of\s+treatments?)/i,
      // £X - £Y range
      /(£[\d,]+\s*-\s*£[\d,]+)/i
    ];
    
    for (const pattern of salaryPatterns) {
      const match = description.match(pattern);
      if (match && match[1]) {
        salary = match[1].trim();
        console.log('Found salary:', salary);
        break;
      }
    }
    
    // UPDATED: Use centralized employment type classification
    const employmentType = extractEmploymentType({
      title: title,
      description: description,
      content: title + ' ' + description
    });
    
    console.log('Classified employment type:', employmentType);
    
    // Extract practice/company name
    let practiceInfo = '';
    const practicePatterns = [
      // "Company Name:" at start
      /^([^:]+):/,
      // "Bupa Dental Care" etc
      /(Bupa\s+Dental\s+Care|ZEST\s+Dental\s+Recruitment|[A-Z][a-zA-Z\s]+Dental[A-zA-Z\s]*)/i
    ];
    
    for (const pattern of practicePatterns) {
      const match = description.match(pattern);
      if (match && match[1]) {
        practiceInfo = match[1].trim();
        break;
      }
    }
    
    // FIXED: Add missing cleanText function
    const cleanText = (text) => {
      return text
        .replace(/\s+/g, ' ')
        .replace(/\n+/g, ' ')
        .trim();
    };
    
    // Enhanced description - try to extract the main job description
    let cleanDescription = description;
    
    // Remove the location from the end if we found it there
    if (location) {
      cleanDescription = description.replace(new RegExp(`\\s+${location.replace(/[()]/g, '\\$&')}\\s*$`), '');
    }
    
    // Try to extract the main descriptive part (usually after the first colon)
    const descParts = cleanDescription.split(':');
    if (descParts.length > 2) {
      // Take everything after first colon, but before any "Apply" or similar
      cleanDescription = descParts.slice(1).join(':')
        .replace(/Apply.*$/i, '')
        .replace(/Contact.*$/i, '')
        .trim();
    }
    
    return {
      title: cleanText(cleanTitle).substring(0, 200),
      description: cleanText(cleanDescription).substring(0, 1000),
      location: cleanText(location).substring(0, 100),
      salary: cleanText(salary).substring(0, 100),
      employmentType: employmentType, // Use classified result
      practiceInfo: cleanText(practiceInfo).substring(0, 200),
      applicationInstructions: `Apply online at: ${rssItem.link}`, // FIXED: Use rssItem.link instead of undefined jobUrl
      originalUrl: rssItem.link, // FIXED: Use rssItem.link instead of undefined jobUrl
      role: detectedRole,
      scrapingStatus: 'RSS_ENHANCED'
    };
  };

  // NEW: Function to clean up expired RSS jobs
  const cleanupExpiredRSSJobs = async () => {
    try {
      setCurrentStep('Cleaning up expired RSS jobs (older than 45 days)...');
      setProgress(5);
      
      // Calculate cutoff date (45 days ago)
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 45);
      const cutoffTimestamp = Timestamp.fromDate(cutoffDate);
      
      console.log('Cleaning up RSS jobs posted before:', cutoffDate.toISOString());
      
      // Query for expired RSS jobs
      const expiredJobsQuery = query(
        collection(db, 'rss_jobs'),
        where('postedAt', '<', cutoffTimestamp)
      );
      
      const expiredSnapshot = await getDocs(expiredJobsQuery);
      
      if (expiredSnapshot.empty) {
        console.log('No expired RSS jobs found to clean up');
        return { deletedCount: 0 };
      }
      
      console.log(`Found ${expiredSnapshot.size} expired RSS jobs to delete`);
      
      // Use batch delete for efficiency (max 500 per batch)
      const batches = [];
      let currentBatch = writeBatch(db);
      let operationCount = 0;
      
      expiredSnapshot.docs.forEach((docSnapshot) => {
        currentBatch.delete(docSnapshot.ref);
        operationCount++;
        
        // Firestore batch limit is 500 operations
        if (operationCount === 500) {
          batches.push(currentBatch);
          currentBatch = writeBatch(db);
          operationCount = 0;
        }
      });
      
      // Add the final batch if it has operations
      if (operationCount > 0) {
        batches.push(currentBatch);
      }
      
      // Execute all batches
      for (let i = 0; i < batches.length; i++) {
        setCurrentStep(`Deleting expired jobs (batch ${i + 1} of ${batches.length})...`);
        await batches[i].commit();
      }
      
      console.log(`Successfully deleted ${expiredSnapshot.size} expired RSS jobs`);
      
      return { deletedCount: expiredSnapshot.size };
      
    } catch (error) {
      console.error('Error cleaning up expired RSS jobs:', error);
      return { deletedCount: 0, error: error.message };
    }
  };

  const processRSSJobs = async (sourceKey) => {
    const source = RSS_SOURCES[sourceKey];
    setProcessing(true);
    setProgress(0);
    setResults(null);
    
    const stats = {
      total: 0,
      processed: 0,
      saved: 0,
      skipped: 0,
      errors: 0,
      geocodedCount: 0,
      geocodedWithPostcode: 0,
      geocodedWithLocation: 0,
      rssEnhancedCount: 0,
      roleBreakdown: {}, // Track jobs by role
      deletedExpiredCount: 0 // NEW: Track deleted expired jobs
    };

    try {
      // NEW: Step 0: Clean up expired RSS jobs first
      const cleanupResult = await cleanupExpiredRSSJobs();
      stats.deletedExpiredCount = cleanupResult.deletedCount;
      
      if (cleanupResult.error) {
        console.warn('Cleanup had issues but continuing:', cleanupResult.error);
      }

      // Step 1: Fetch RSS feed
      setCurrentStep(`Fetching ${source.name} RSS feed...`);
      const rssItems = await fetchRSSFeed(source);
      stats.total = rssItems.length;
      
      // Step 2: Process each job - focus on RSS extraction instead of scraping
      setCurrentStep(`Found ${stats.total} ${source.role} jobs. Processing with enhanced RSS extraction...`);      
      for (let i = 0; i < rssItems.length; i++) {
        const item = rssItems[i];
        setCurrentStep(`Processing ${source.role} job ${i + 1} of ${stats.total}: ${item.title.substring(0, 50)}...`);
        setProgress(20 + (i / stats.total) * 70);
        
        let jobData = null;
        
        try {
          // Check if we already have this job (by URL)
          const existingJobQuery = query(
            collection(db, 'rss_jobs'),
            where('originalUrl', '==', item.link)
          );
          const existingJob = await getDocs(existingJobQuery);
          
          if (!existingJob.empty) {
            stats.skipped++;
            continue;
          }
        } catch (error) {
          console.error('Error checking existing job:', error);
        }
        
        // Use enhanced RSS extraction as primary method with role information
        setCurrentStep(`Extracting ${source.role} details from RSS: ${item.title.substring(0, 50)}...`);
        jobData = extractJobDetailsFromRSS(item, source.role);
        
        // Track role breakdown
        if (!stats.roleBreakdown[jobData.role]) {
          stats.roleBreakdown[jobData.role] = 0;
        }
        stats.roleBreakdown[jobData.role]++;
        
        // Geocode location
        let postcode = null;
        let latitude = null;    
        let longitude = null;
        let geocodeSource = null;
        
        // First, try to find a postcode
        const locationSources = [
          jobData.location,
          jobData.description,
          item.title,
        ].filter(Boolean);
        
        for (const locationSource of locationSources) {
          postcode = extractPostcode(locationSource);
          if (postcode) {
            console.log('Found postcode for geocoding:', postcode);
            break;
          }
        }
        
        if (postcode) {
          // Try geocoding with postcode first
          setCurrentStep(`Geocoding postcode ${postcode}...`);   const coords = await geocodePostcode(postcode);
          try {
            console.log('Postcode geocoding result:', coords);
            
            if (coords && coords.latitude && coords.longitude) {
              latitude = coords.latitude;
              longitude = coords.longitude;
              geocodeSource = 'postcode';
              stats.geocodedCount++;
              stats.geocodedWithPostcode++;
              console.log('Successfully geocoded postcode:', postcode, 'to', latitude, longitude);
            } else {
              console.warn('Postcode geocoding returned no coordinates for:', postcode);
            }
          } catch (geoError) {
            console.error('Postcode geocoding failed for', postcode, geoError);
          }
        } else {
          // If postcode geocoding failed or no postcode, try location name
          try {
            const coords = await geocodeLocation(jobData.location.trim());
            console.log('Location geocoding result:', coords);
            
            if (coords && coords.latitude && coords.longitude) {
              latitude = coords.latitude;
              longitude = coords.longitude;
              geocodeSource = coords.method || 'location_fallback';
              stats.geocodedCount++;
              stats.geocodedWithLocation++;
              console.log('Successfully geocoded location:', jobData.location, 'to', latitude, longitude, 'via', coords.method);
            } else {
              console.warn('Location geocoding returned no coordinates for:', jobData.location);
            }
          } catch (geoError) {
            console.error('Location geocoding failed for', jobData.location, geoError);
          }
        }
        
        // Prepare job data for saving with role information
        const jobDataToSave = {
          source: source.type,
          originalUrl: item.link,
          title: jobData.title,
          description: jobData.description,
          location: jobData.location,
          postcode: postcode,
          latitude: latitude,
          longitude: longitude,
          geocodeSource: geocodeSource,
          employmentType: jobData.employmentType,
          salary: jobData.salary,
          practiceInfo: jobData.practiceInfo,
          applicationInstructions: jobData.applicationInstructions,
          postedAt: item.pubDate ? new Date(item.pubDate) : new Date(),
          scrapedAt: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          isActive: true,
          scrapingStatus: jobData.scrapingStatus,
          scrapingNote: `Extracted from ${source.name} RSS feed with enhanced parsing`,
          needsManualReview: false,
        };
        
        // Save to Firestore with enhanced RSS data
        try {
          await addDoc(collection(db, 'rss_jobs'), jobDataToSave);
          stats.saved++;
        } catch (error) {
          console.error('Error saving job:', error);
          stats.errors++;
        }
      }
      
      setResults(stats);
      toast({
        title: `${source.name} Processing Complete`,
        description: `Found ${stats.total} ${source.role} jobs in RSS feed. ${stats.saved} new jobs added${stats.deletedExpiredCount > 0 ? `, ${stats.deletedExpiredCount} expired jobs removed` : ''}. ${stats.geocodedCount} jobs successfully geocoded.`,
        variant: "success"
      });

      // FIXED: Refresh the job list after successful processing
      if (stats.saved > 0) {
        console.log('Refreshing job list after processing', stats.saved, 'new jobs');
        await loadRecentRSSJobs();
      }

    } catch (error) {
      console.error('RSS processing error:', error);
      toast({
        title: `${source.name} Processing Failed`,
        description: error.message,
        variant: "destructive"
      });
      setResults({ ...stats, error: error.message });
    } finally {
      setProcessing(false);
      setCurrentStep('');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setShowEditModal(true);
  };

  const handleSaveJobEdit = async (updatedJobData) => {
    try {
      const jobRef = doc(db, 'rss_jobs', editingJob.id);
      let updateData = { ...updatedJobData };
      
      if (updatedJobData.location !== editingJob.location && updatedJobData.location?.trim()) {
        setCurrentStep('Geocoding updated location...');
        
        // Try postcode first
        const newPostcode = extractPostcode(updatedJobData.location);
        if (newPostcode) {
          try {
            const coords = await geocodePostcode(newPostcode);
            if (coords && coords.latitude && coords.longitude) {
              updateData.latitude = coords.latitude;
              updateData.longitude = coords.longitude;
              updateData.postcode = newPostcode;
              updateData.geocodeSource = 'postcode_manual';
            }
          } catch (error) {
            console.warn('Manual postcode geocoding failed:', error);
          }
        }
        
        // Try location name if postcode failed
        if (!updateData.latitude || !updateData.longitude) {
          try {
            const coords = await geocodeLocation(updatedJobData.location.trim());
            if (coords && coords.latitude && coords.longitude) {
              updateData.latitude = coords.latitude;
              updateData.longitude = coords.longitude;
              updateData.geocodeSource = 'location_manual';
            }
          } catch (error) {
            console.warn('Manual location geocoding failed:', error);
          }
        }
      }
      
      // Mark as human reviewed
      updateData.humanReviewed = true;
      updateData.needsManualReview = false;
      updateData.lastUpdated = serverTimestamp();
      
      await updateDoc(jobRef, updateData);
      
      toast({
        title: "Job Updated",
        description: "Job details have been successfully updated.",
        variant: "success"
      });
      
      setShowEditModal(false);
      setEditingJob(null);
      // FIXED: This already refreshes the list, which is correct
      await loadRecentRSSJobs(); // Refresh the list
      
    } catch (error) {
      console.error('Error updating job:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update job details.",
        variant: "destructive"
      });
    }
  };

  // --- Add hard delete function ---
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to permanently delete this job? This cannot be undone.')) return;
    try {
      await import('firebase/firestore').then(({ doc, deleteDoc }) =>
        deleteDoc(doc(db, 'rss_jobs', jobId))
      );
      toast({
        title: "Job Deleted",
        description: "The job has been permanently deleted.",
        variant: "success"
      });
      setRecentJobs(prev => prev.filter(j => j.id !== jobId));
    } catch (error) {
      console.error('Error deleting job:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete job.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <Card>
        <CardHeader>
          <CardTitle>RSS Job Manager</CardTitle>
          <CardDescription>
            Manage and process job listings from RSS feeds for different dental roles.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Multiple RSS source buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Button
                  onClick={() => processRSSJobs('BDJ_HYGIENIST_THERAPIST')}
                  disabled={processing}
                  className={cn(
                    "w-full",
                    theme === 'light' ? 'bg-purple-700 text-white hover:bg-purple-800' : ''
                  )}
                >
                  {processing ? 'Processing...' : 'Import Hygienist/Therapist Jobs'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  BDJ Jobs - Dental Hygienist & Therapist positions
                </p>
              </div>
              
              <div>
                <Button
                  onClick={() => processRSSJobs('BDJ_LOCUM_DENTIST')}
                  disabled={processing}
                  className="w-full"
                  variant="outline"
                >
                  {processing ? 'Processing...' : 'Import Locum Dentist Jobs'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  BDJ Jobs - Locum Dentist positions
                </p>
              </div>
              
              <div>
                <Button
                  onClick={() => processRSSJobs('BDJ_DENTAL_NURSE')}
                  disabled={processing}
                  className="w-full"
                  variant="outline"
                >
                  {processing ? 'Processing...' : 'Import Dental Nurse Jobs'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  BDJ Jobs - Dental Nurse positions
                </p>
              </div>
            </div>
            
            {/* Bulk import button */}
            <div className="border-t pt-4">
              <Button
                onClick={async () => {
                  for (const sourceKey of Object.keys(RSS_SOURCES)) {
                    if (!processing) {
                      await processRSSJobs(sourceKey);
                    }
                  }
                  // FIXED: Refresh job list after bulk processing
                  console.log('Refreshing job list after bulk processing');
                  await loadRecentRSSJobs();
                }}
                disabled={processing}
                className={cn(
                  "w-full",
                  theme === 'light' ? 'bg-purple-700 text-white hover:bg-purple-800' : ''
                )}
                variant="default"
              >
                {processing ? 'Processing...' : 'Import All Job Types'}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Import from all RSS sources (Hygienist/Therapist, Locum Dentist, and Dental Nurse)
              </p>
            </div>
            
            {currentStep && (
              <div className="text-sm text-muted-foreground">
                <p>{currentStep}</p>
                <Progress value={progress} className="h-2" />
              </div>
            )}
            
            {results && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
                  <div className="text-center">
                    <div className={cn("text-2xl font-bold", theme === 'light' ? 'text-gray-900' : 'text-white')}>
                      {results.total}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Total Found
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {results.saved}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Jobs Saved
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {results.skipped}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Skipped (Existing)
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {results.rssEnhancedCount || 0}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Enhanced RSS
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {results.geocodedCount || 0}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Total Geocoded
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-teal-600">
                      {results.geocodedWithPostcode || 0}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Via Postcode
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-cyan-600">
                      {results.geocodedWithLocation || 0}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Via Location
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {results.errors}
                    </div>
                    <div className={cn("text-sm", theme === 'light' ? 'text-gray-600' : 'text-gray-400')}>
                      Errors
                    </div>
                  </div>
                </div>
                
                {/* Role breakdown display */}
                {results.roleBreakdown && Object.keys(results.roleBreakdown).length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Jobs by Role:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {Object.entries(results.roleBreakdown).map(([role, count]) => (
                        <div key={role} className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                          <div className="text-lg font-bold text-blue-600">{count}</div>
                          <div className="text-xs text-blue-700 dark:text-blue-300">{role}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {(results.rssEnhancedCount > 0 || results.geocodedCount > 0 || results.deletedExpiredCount > 0 || results.error) && (
                  <div className="space-y-2">
                    {/* NEW: Show cleanup results */}
                    {results.deletedExpiredCount > 0 && (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                        <p className="text-orange-700 text-sm">
                          🗑️ Cleaned up {results.deletedExpiredCount} RSS jobs that were older than 45 days to keep the database fresh.
                        </p>
                      </div>
                    )}
                    {results.rssEnhancedCount > 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                        <p className="text-green-700 text-sm">
                          ✅ Successfully used enhanced RSS extraction to parse location, salary, and job details 
                          from {results.rssEnhancedCount} job descriptions. 
                          {results.geocodedCount > 0 && ` ${results.geocodedCount} jobs were geocoded with coordinates.`}
                        </p>
                      </div>
                    )}
                    {results.error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-700 text-sm">{results.error}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Recent RSS Jobs with role information */}
      <Card className={cn(
        theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#2A2A3E] border-purple-500/20'
      )}>
        <CardHeader>
          <CardTitle className={cn(
            "flex items-center gap-2",
            theme === 'light' ? 'text-gray-900' : 'text-white'
          )}>
            <Clock className="h-5 w-5" />
            All RSS Jobs ({recentJobs.length})
            {recentJobs.filter(job => job.needsLocationReview).length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {recentJobs.filter(job => job.needsLocationReview).length} need review
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            All jobs from RSS sources across different dental roles. Jobs without location data are flagged for review.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentJobs.length === 0 ? (
            <p className={cn(
              "text-center py-8",
              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
            )}>
              No RSS jobs found in database. Click "Import" buttons to get started.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Add pagination info at the top with role breakdown */}
              <div className={cn(
                "text-sm px-3 py-2 rounded-md",
                theme === 'light' ? 'bg-blue-50 text-blue-700' : 'bg-blue-900/20 text-blue-300'
              )}>
                Showing all {recentJobs.length} RSS jobs from database
                {recentJobs.filter(job => job.needsLocationReview).length > 0 && (
                  <span className="ml-2">
                    • {recentJobs.filter(job => job.needsLocationReview).length} need location review
                  </span>
                )}
                
                {/* Role breakdown for current jobs */}
                {(() => {
                  const roleBreakdown = {};
                  recentJobs.forEach(job => {
                    const role = job.role || 'Unknown';
                    roleBreakdown[role] = (roleBreakdown[role] || 0) + 1;
                  });
                  return (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(roleBreakdown).map(([role, count]) => (
                        <span key={role} className="px-2 py-1 bg-white/10 rounded text-xs">
                          {role}: {count}
                        </span>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {recentJobs.map((job) => (
                <div
                  key={job.id}
                  className={cn(
                    "p-4 border rounded-lg relative",
                    job.needsLocationReview 
                      ? theme === 'light' 
                        ? 'border-orange-200 bg-orange-50' 
                        : 'border-orange-800 bg-orange-900/20'
                      : theme === 'light' 
                        ? 'border-gray-200' 
                        : 'border-gray-700'
                  )}
                >
                  {/* Status indicators */}
                  <div className="absolute top-2 right-2 flex gap-1">
                    {job.needsLocationReview && (
                      <AlertTriangle className="h-4 w-4 text-orange-500" title="Needs location review" />
                    )}
                    {job.humanReviewed && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" title="Human reviewed" />
                    )}
                  </div>

                  <div className="flex items-start justify-between mb-2 pr-8">
                    <div className="flex-1">
                      <h3 className={cn(
                        "font-medium",
                        theme === 'light' ? 'text-gray-900' : 'text-white'
                      )}>
                        {job.title}
                      </h3>
                      {job.role && (
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {job.role}
                        </Badge>
                      )}
                      {/* NEW: Show non-UK badge */}
                      {job.isNonUK && (
                        <Badge variant="destructive" className="ml-2">
                          Non-UK
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditJob(job)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      {/* NEW: Hard delete button for admin */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteJob(job.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                        title="Delete job permanently"
                      >
                        <XCircle className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    {job.location ? (
                      <div className={cn(
                        "flex items-center gap-1",
                        theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      )}>
                        <MapPin className="h-3 w-3" />
                        {job.location}
                        {job.postcode && (
                          <span className="text-xs text-muted-foreground ml-1">({job.postcode})</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-orange-600">
                        <AlertTriangle className="h-3 w-3" />
                        <span className="text-xs">No location identified</span>
                      </div>
                    )}

                    {job.salary && (
                      <div className={cn(
                        theme === 'light' ? 'text-gray-600' : 'text-gray-400'
                      )}>
                        {job.salary}
                      </div>
                    )}

                    {job.employmentType && (
                      <Badge variant="outline" className="text-xs">
                        {job.employmentType}
                      </Badge>
                    )}

                    {/* Geocoding status */}
                    {job.latitude && job.longitude ? (
                      <div className={cn(
                        "text-xs px-2 py-1 rounded flex items-center gap-1",
                        job.geocodeSource?.includes('postcode') 
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                          : "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400"
                      )}>
                        📍 {job.geocodeSource?.includes('manual') ? 'Manual ' : ''}{job.geocodeSource?.includes('postcode') ? 'Postcode' : 'Location'}
                      </div>
                    ) : (
                      <div className="text-xs px-2 py-1 rounded bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                        📍 No coordinates
                      </div>
                    )}

                    <div className={cn(
                      "flex items-center gap-1",
                      theme === 'light' ? 'text-gray-500' : 'text-gray-500'
                    )}>
                      <Clock className="h-3 w-3" />
                      {/* FIXED: Show postedAt date when available, fallback to scrapedAt */}
                      {job.postedAt ? (
                        job.postedAt instanceof Date ? job.postedAt.toLocaleDateString() :
                        job.postedAt.seconds ? new Date(job.postedAt.seconds * 1000).toLocaleDateString() :
                        new Date(job.postedAt).toLocaleDateString()
                      ) : (
                        job.scrapedAt && new Date(job.scrapedAt.seconds * 1000).toLocaleDateString()
                      )}
                    </div>
                  </div>

                  {job.originalUrl && (
                    <a
                      href={job.originalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-700 text-xs mt-2 inline-block"
                    >
                      View Original →
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Edit Modal */}
      {showEditModal && editingJob && (
        <JobEditModal
          job={editingJob}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingJob(null);
          }}
          onSave={handleSaveJobEdit}
        />
      )}
    </div>
  );
};

export default RSSJobManagerPage;
